/**
 * Derivações puras do "Por região" (§7.12): uma linha por região, em ordem FIXA (N, NE, CO, SE, S, EX; AC-7.12-01),
 * com o líder da região, o texto da 2ª linha e a variação contra o 2º turno de 2022 (mesmo partido, mesma região).
 * Nada aqui ordena por votos (N01) nem soma UFs: a região vem pronta de `view.regions` (§14.8.9). O mesmo modelo de
 * texto vale para os dois partidos (N03): trocar 13 e 22 nos dados troca só sigla, nome de 2022 e números.
 */
import type { Baseline2022, Geography } from '../../contracts/static';
import { type CopyKey, t } from '../../copy/strings';
import { fmtDelta, fmtPct, fmtPoints, fmtQtyShort, fmtSections, fmtVoters, MINUS } from '../../format/numbers';
import type { Candidate, PlaceView, RegionCode, ResultsView } from '../../model/types';

/** Ordem fixa das linhas (§7.12 bloco 2, AC-7.12-01), quaisquer que sejam os dados. */
export const REGION_ORDER: readonly RegionCode[] = Object.freeze(['N', 'NE', 'CO', 'SE', 'S', 'EX']);

/** Chave do nome da região no catálogo (§13.6.5: `region.N` … `region.EX`). */
export const regionNameKey = (code: RegionCode): CopyKey => `region.${code}` as CopyKey;

/** Variação contra 2022 (§7.12 bloco 2): `share(líder) − share2022(mesmo partido, mesma região)`. */
export interface RegionDelta {
  /** Fração (−0,075 → "−7,5"). */
  value: number;
  /** `regions.delta.title`. */
  title: string;
  /** Sigla do partido do líder (variável do texto acessível). */
  party: string;
}

export interface RegionLeader {
  candidate: Candidate;
  /** Fração dos válidos (0–1). */
  share: number;
}

export interface RegionRowModel {
  code: RegionCode;
  /** "Norte" … "Exterior". */
  name: string;
  /** L2C1: "{S} · faltam {Q}", "100%" ou "urnas fechadas em {N} de {M} cidades". */
  sub: string;
  /** `title` da 2ª linha (forma longa, `regions.row.sub.title`), só com eleitores por apurar. */
  subTitle: string | null;
  /** Líder com votos (null: sem votos, empate ou antes das 17h00). */
  leader: RegionLeader | null;
  /** Sem `Baseline2022`, sem líder ou sem dado de 2022 da região: null (estado "Sem Baseline2022": sem delta). */
  delta: RegionDelta | null;
  /** Nome acessível da linha clicável do Exterior (`regions.row.aria` / `regions.abroad.aria.closed`), ou null. */
  aria: string | null;
  /** Exterior sem votos com a contagem de cidades (`regions.abroad.closed`). */
  closed: boolean;
}

/** Cidades do exterior com urnas fechadas no instante (`closes_t_min ≤ instante`, §7.12 bloco 3; AC-7.12-05). */
export function abroadClosed(geo: Geography | null, instantTMin: number): { n: number; m: number } | null {
  if (!geo || geo.abroad_cities.length === 0) return null;
  let n = 0;
  for (const c of geo.abroad_cities) if (c.closes_t_min <= instantTMin) n++;
  return { n, m: geo.abroad_cities.length };
}

/** Eleitorado de referência da região pela geografia (antes do 1º snapshot, para o "faltam {Q}"). */
export function regionElectorateRef(geo: Geography | null, code: RegionCode): number | null {
  if (!geo) return null;
  const places = geo.regions.find((r) => r.code === code)?.places;
  if (!places) return null;
  let sum = 0;
  for (const pc of places) sum += geo.places.find((p) => p.code === pc)?.electorate_ref ?? 0;
  return sum;
}

/** Variação contra o 2º turno de 2022 (§7.12 bloco 2; AC-7.12-02/03). */
export function regionDelta(
  code: RegionCode,
  leader: RegionLeader,
  baseline: Baseline2022 | null | undefined,
): RegionDelta | null {
  if (!baseline) return null;
  const num = baseline.party_to_number[leader.candidate.party_code];
  const tally = baseline.regions[code];
  if (num === undefined || !tally || !(tally.valid_votes > 0)) return null;
  const share2022 = tally.votes[num] / tally.valid_votes;
  if (!Number.isFinite(share2022)) return null;
  const who2022 = baseline.candidates.find((c) => c.number === num);
  if (!who2022) return null;
  return {
    value: leader.share - share2022,
    party: leader.candidate.party_code,
    title: t('regions.delta.title', { SIGLA: leader.candidate.party_code, NOME2022: who2022.display_name }),
  };
}

/** Delta exibido: "+3,7" · "−2,1" (U+2212) · "0,0" (§13.4.2 `fmtDelta`). */
export const deltaText = (v: number): string => fmtDelta(v);

/** Cópia acessível do delta (`regions.delta.sr.*`): "mais 3,7 pontos em relação ao PT no 2º turno de 2022". */
export function deltaSr(v: number, party: string): string {
  const less = fmtDelta(v).startsWith(MINUS);
  return t(less ? 'regions.delta.sr.less' : 'regions.delta.sr.more', { pontos: fmtPoints(v, 1), SIGLA: party });
}

/** Percentual do líder na linha: 1 casa (§7.12, "52,4%"). */
export const leaderPct = (v: number): string => fmtPct(v, 1);

/** "{S} · faltam {Q}", ou só "100%" com a região completa (§13.6.5 `regions.row.sub`). */
function subOf(p: PlaceView | null, electorateRef: number | null): { sub: string; title: string | null } {
  if (p && (p.status === 'complete' || p.sectionsFraction >= 1)) return { sub: fmtSections(1), title: null };
  const remaining = p ? p.remainingElectorate : electorateRef;
  const s = fmtSections(p ? p.sectionsFraction : 0);
  if (remaining === null) return { sub: s, title: null };
  return {
    sub: t('regions.row.sub', { S: s, Q: fmtQtyShort(remaining) }),
    title: t('regions.row.sub.title', { E: fmtVoters(remaining) }),
  };
}

export interface RegionInputs {
  view: ResultsView | null;
  /** Antes das 17h00 ou sem snapshot (manchete pré-resultado, E01): nenhum percentual de candidato (AC-7.17-03). */
  pre: boolean;
  geography: Geography | null;
  baseline: Baseline2022 | null | undefined;
  /** Instante para as urnas fechadas no exterior (ao vivo: o relógio da fonte; replay: o instante exibido). */
  abroadTMin: number;
}

/** Modelo de uma linha (§7.12 blocos 2–4 e 7). */
export function regionRow(code: RegionCode, inp: RegionInputs): RegionRowModel {
  const name = t(regionNameKey(code));
  const p = inp.view?.regions[code] ?? null;
  const hasVotes = !inp.pre && p !== null && p.validVotes > 0;
  const idx = hasVotes ? p.leader : null;
  const leader: RegionLeader | null =
    idx === null || inp.view === null ? null : { candidate: inp.view.candidates[idx], share: p?.shares[idx] ?? 0 };
  const delta = leader ? regionDelta(code, leader, inp.baseline) : null;
  if (code === 'EX' && !hasVotes) {
    const closed = abroadClosed(inp.geography, inp.abroadTMin);
    if (closed) {
      return {
        code,
        name,
        sub: t('regions.abroad.closed', { N: closed.n, M: closed.m }),
        subTitle: null,
        leader: null,
        delta: null,
        aria: t('regions.abroad.aria.closed', { N: closed.n, M: closed.m }),
        closed: true,
      };
    }
  }
  const { sub, title } = subOf(p, p ? null : regionElectorateRef(inp.geography, code));
  const aria =
    code === 'EX' && leader && p
      ? t('regions.row.aria', {
          REGIAO: name,
          X: leader.candidate.display_name,
          pX: leaderPct(leader.share),
          S: fmtSections(p.sectionsFraction),
        })
      : null;
  return { code, name, sub, subTitle: title, leader, delta, aria, closed: false };
}

/** As 6 linhas, sempre na ordem fixa. */
export function regionRows(inp: RegionInputs): RegionRowModel[] {
  return REGION_ORDER.map((code) => regionRow(code, inp));
}
